/**
 * AdventureCtaPanel - persistent "Begin Your Adventure" card
 *
 * Lives at the TOP of the lobby's right column, always visible once the
 * player has a character. One tap fires the server-side quick-start
 * composition (auto-party + default module + start / resume); the passive
 * states (waiting for leader, module vote pending) render guidance instead
 * of a button. State derivation is pure, see adventure-cta-state.ts.
 */

import { useCallback, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Swords, Hourglass } from 'lucide-react';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { useLobbyStore, useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { websocketService } from '@/lib/websocket-service';
import { deriveAdventureCta } from './adventure-cta-state';

/**
 * WS-drop backstop: if neither `lobby:quick-start-result` nor
 * `lobby:game-started` lands within this window, re-enable the button.
 */
const QUICK_START_PENDING_TIMEOUT_MS = 20_000;

interface AdventureCtaPanelProps {
  className?: string;
}

export default function AdventureCtaPanel({ className }: AdventureCtaPanelProps) {
  const selectedCharacter = useSelectedCharacter();
  const parties = useLobbyStore((state) => state.parties);
  const quickStartPending = useLobbyStore((state) => state.quickStartPending);
  const setQuickStartPending = useLobbyStore((state) => state.setQuickStartPending);

  const characterParty = selectedCharacter
    ? (parties.find((p) => p.memberCharacterIds.includes(selectedCharacter.id)) ?? null)
    : null;

  const cta = deriveAdventureCta({
    hasCharacter: !!selectedCharacter,
    quickStartPending,
    party: characterParty
      ? {
          isLeader: characterParty.leaderCharacterId === selectedCharacter?.id,
          leaderName:
            characterParty.members?.find((m) => m.id === characterParty.leaderCharacterId)?.name ??
            null,
          memberCount: characterParty.memberCharacterIds.length,
          hasModule: !!characterParty.selectedModuleId,
          hasActiveSession: !!characterParty.activeGameSessionId,
          hasPlayedBefore:
            (characterParty.completedModules?.length ?? 0) > 0 || !!characterParty.lastSessionEnd,
        }
      : null,
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (quickStartPending) {
      timeoutRef.current = setTimeout(() => {
        console.warn('[quick-start] no result within timeout, resetting pending state');
        setQuickStartPending(false);
      }, QUICK_START_PENDING_TIMEOUT_MS);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [quickStartPending, setQuickStartPending]);

  const handleQuickStart = useCallback(() => {
    setQuickStartPending(true);
    websocketService.quickStartAdventure();
  }, [setQuickStartPending]);

  if (cta.kind === 'hidden') {
    return null;
  }

  const isReturningLaunch = (cta.kind === 'start' || cta.kind === 'quick-start') && cta.returning;
  const buttonLabel =
    cta.kind === 'resume'
      ? 'Resume Adventure'
      : isReturningLaunch
        ? 'Continue Your Adventure'
        : 'Begin Your Adventure';

  return (
    <Card className={className} data-testid="adventure-cta-panel">
      <CardContent className="p-3 space-y-2">
        {cta.kind === 'waiting-leader' ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Hourglass className="h-4 w-4 shrink-0 text-accent-gold" />
            <span>
              {cta.leaderName
                ? `${cta.leaderName} leads the party and opens the portal when everyone is ready.`
                : 'The party leader opens the portal when everyone is ready.'}
            </span>
          </div>
        ) : cta.kind === 'pick-module' ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Swords className="h-4 w-4 shrink-0 text-accent-gold" />
            <span>Choose an adventure with your party in party chat, then start from here.</span>
          </div>
        ) : (
          <Button
            id="begin-adventure-cta"
            className="w-full bg-accent-gold text-background font-semibold hover:bg-accent-gold/90"
            size="lg"
            disabled={cta.kind === 'pending'}
            onClick={handleQuickStart}
          >
            {cta.kind === 'pending' ? (
              <span className="flex items-center gap-2">
                <AnkhSpinner size="sm" className="text-background" />
                Opening the portal...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Swords className="h-5 w-5" />
                {buttonLabel}
              </span>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
